import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    declaredLang,
    explicitLang,
    loadChoice,
    loadVisited,
    resolveLanguage,
    saveChoice,
    saveVisited,
    switchAction,
} from "@/i18n/language";
import { SUPPORTED } from "@/i18n/languages";
import { HTML_LANG } from "@/seo/locale";
import { href } from "@/i18n/paths";
import { translate } from "@/i18n/strings";

/**
 * La langue de l'interface, pour l'arbre entier.
 *
 * Monté sous le routeur — donc sous BrowserRouter côté navigateur et sous
 * StaticRouter au prérendu — il lit le chemin courant pour savoir si la page
 * impose sa langue (page de contenu) ou la laisse se déduire (page de
 * l'application).
 *
 * Le navigateur et la mémoire de session sont lus dès le premier rendu, derrière
 * des gardes qui rendent null sous node, où ils n'existent pas : le prérendu de
 * /new retombe ainsi sur la langue du site. Le HTML prérendu peut différer de
 * ce que le navigateur affiche ensuite sans conséquence — index.js rend depuis
 * zéro avec createRoot, il n'hydrate pas — et les seules pages concernées sont
 * hors index. Le choix mémorisé, lui, n'arrive qu'après le montage ; RootRedirect
 * (App.js) le relit directement pour ne pas naviguer avant lui.
 */

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const routeLang = declaredLang(pathname);

    // La langue de la dernière page de contenu qui la revendique — en ou de.
    // Le temps de la page chargée, sans persistance : persistée, elle
    // deviendrait un choix que l'utilisateur n'a jamais fait.
    const originLang = useRef(explicitLang(pathname));
    const [choice, setChoice] = useState(null);

    useEffect(() => {
        setChoice(loadChoice());
    }, []);

    // Seule une langue revendiquee par la route compte comme origine : une
    // page francaise visitee faute de traduction n'est pas un choix.
    const claimed = explicitLang(pathname);
    useEffect(() => {
        if (claimed) originLang.current = claimed;
    }, [claimed]);

    // La derniere page de contenu traversee, francais implicite compris. Elle
    // ne pese qu'apres le navigateur, quand celui-ci ne parle aucune des trois
    // langues : c'est alors le seul indice donne par un visiteur entre par
    // dartsync.fr, et il vaut mieux que le repli anglais. En memoire de session
    // pour survivre au rechargement d'une partie en cours.
    const visitedLang = useRef(routeLang || loadVisited());
    useEffect(() => {
        if (routeLang) {
            visitedLang.current = routeLang;
            saveVisited(routeLang);
        }
    }, [routeLang]);

    const lang = resolveLanguage({
        routeLang,
        choice,
        originLang: originLang.current,
        visitedLang: visitedLang.current,
    });

    // L'attribut lang du document suit la langue affichee, pas celle de l'URL :
    // sur une page de l'application, un lecteur d'ecran annoncerait sinon du
    // francais devant une interface anglaise.
    useEffect(() => {
        if (typeof document !== "undefined") {
            document.documentElement.setAttribute("lang", HTML_LANG[lang] || lang);
        }
    }, [lang]);

    /**
     * Basculer la langue. `switchAction` decide, ce module execute.
     *
     * Le choix n'est memorise que sur un ecran de l'application, ou le clic
     * change visiblement l'interface : c'en est un. Sur une page de contenu,
     * le geste n'a l'air que d'un changement de page, et l'ecrire dans le
     * stockage donnait une application allemande, d'une visite a l'autre, a
     * qui n'avait fait que jeter un oeil a une page. La langue suit quand meme,
     * portee par `originLang` — le troisieme etage de `resolveLanguage` suffit,
     * et c'est mesurable : arriver sur /new apres /de/dart-regeln rend
     * l'allemand sans aucun choix memorise.
     */
    const setLang = useCallback(
        (next) => {
            const action = switchAction(pathname, next);
            if (action.kind === "choose") {
                setChoice(next);
                saveChoice(next);
            } else if (action.kind === "navigate") {
                navigate(action.to);
            }
        },
        [navigate, pathname]
    );

    const value = useMemo(
        () => ({
            lang,
            setLang,
            t: (key, vars) => translate(lang, key, vars),
            // Le selecteur s'affiche partout : sur une page de l'application il
            // change l'interface, sur une page de contenu il emmene vers la
            // traduction, et sur une page pas encore traduite il ramene a
            // l'accueil de la langue. Le masquer la ou la traduction manque
            // laissait sans issue le visiteur que l'en-tete y avait envoye.
            canSwitch: true,
            // Ou mene chaque langue depuis cette page : une URL sur une page de
            // contenu, rien sur un ecran de l'application, ou la bascule se
            // fait sur place. Le composant s'en sert pour rendre des liens
            // plutot que des boutons quand il y a une destination.
            switchTargets: Object.fromEntries(
                SUPPORTED.map((code) => [code, switchAction(pathname, code).to]).filter(
                    ([, to]) => to
                )
            ),
            hrefFor: (page) => href(page, lang),
        }),
        [lang, setLang, pathname]
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/**
 * La langue courante et de quoi la changer.
 *
 * Hors fournisseur — un composant monté seul dans un test — la valeur retombe
 * sur le français plutôt que de lever : un composant d'interface n'a pas à
 * connaître l'existence du fournisseur pour s'afficher.
 */
export function useLanguage() {
    return (
        useContext(LanguageContext) || {
            lang: "fr",
            setLang: () => {},
            t: (key, vars) => translate("fr", key, vars),
            canSwitch: false,
            switchTargets: {},
            hrefFor: (page) => href(page, "fr"),
        }
    );
}
